import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { BookOpen, ChevronRight } from "lucide-react";

interface Props {
  brand: string;
  model: string;
  /** Optional explicit external review URL (takes precedence over internal lookup) */
  externalUrl?: string | null;
}

/**
 * Finds a published review article matching this product (brand + model in title)
 * and renders a prominent CTA. Falls back to an external expert review URL if provided.
 * Used on product pages to differentiate them from review articles for SEO.
 */
export const MatchingReviewCTA = ({ brand, model, externalUrl }: Props) => {
  const { data: article } = useQuery({
    queryKey: ["matching-review", brand, model],
    queryFn: async () => {
      if (!brand || !model) return null;
      // Match articles whose title contains both brand and model (case-insensitive)
      const { data } = await supabase
        .from("articles")
        .select("slug, title")
        .eq("status", "published")
        .ilike("title", `%${brand}%${model}%`)
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!brand && !!model,
  });

  if (article?.slug) {
    return (
      <section className="mb-10">
        <Link
          to={`/articles/${article.slug}`}
          className="block p-5 rounded-xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="bg-primary/15 rounded-full p-3 shrink-0">
              <BookOpen className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">
                Hands-on Review
              </p>
              <p className="text-base md:text-lg font-bold text-foreground truncate">
                {article.title}
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Read our full verdict, testing notes, and detailed pros &amp; cons.
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-primary shrink-0 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </section>
    );
  }

  if (externalUrl) {
    return (
      <section className="mb-10">
        <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex">
          <Button
            variant="outline"
            size="lg"
            className="gap-2 text-base px-8 border-primary/40 bg-primary/5 hover:bg-primary/10"
          >
            <BookOpen className="h-5 w-5" /> Read full expert review
          </Button>
        </a>
      </section>
    );
  }

  return null;
};
